import { useId, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import { CoreModal, CoreSearchField, CoreTable, CoreTableColumn } from "@verity/components/core";
import i18n from "../../../../../i18n";
import styles from "./InstalledExtensionsModal.module.css";

export function InstalledExtensionsModal({
	open,
	onClose,
	extensions,
}: {
	open: boolean;
	onClose: () => void;
	extensions: string[];
}) {
	const [search, setSearch] = useState("");
	const tableId = useId();

	const filteredExtensions = useMemo(
		() =>
			extensions
				.filter((extension) => (search ? extension.toLowerCase().includes(search.toLowerCase()) : true))
				.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
		[extensions, search],
	);

	const tableData = useMemo(() => filteredExtensions.map((extension) => ({ name: extension })), [filteredExtensions]);

	const columns: CoreTableColumn<{ name: string }>[] = [
		{
			header: (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<Typography>{i18n.t("result.components.resultsPage.extensionModal.browserExtension")}</Typography>
					<div className={styles.badge}>
						<Typography>
							{i18n.t("result.components.resultsPage.extensionModal.tableBadge", { count: extensions.length })}
						</Typography>
					</div>
				</div>
			),
			key: "name",
			descendingComparator: (a, b) => (a.name ?? "").toLowerCase().localeCompare((b.name ?? "").toLowerCase()),
			cell: (item) => item.data.name,
		},
	];

	return (
		<CoreModal
			open={open}
			onClose={onClose}
			showBottomCloseButton={true}
			title={i18n.t("result.components.resultsPage.extensionModal.title")}
			maxWidth="min(90%, 800px)"
			minWidth="min(90%, 800px)"
			maxHeight="92.5%"
		>
			<div className={styles.container}>
				<Typography>{i18n.t("result.components.resultsPage.extensionModal.description")}</Typography>
				<div className={styles.tableContainer}>
					<CoreSearchField
						ariaControls={tableId}
						label={i18n.t("result.components.resultsPage.extensionModal.search")}
						totalResults={filteredExtensions.length ?? 0}
						onSearch={(value) => setSearch(value)}
						value={search}
						fullWidth
					/>
					<CoreTable
						id={tableId}
						columns={columns}
						data={tableData}
						maxHeight="362.5px"
						noResultsString={i18n.t("result.components.resultsPage.extensionModal.noResults")}
						allowUIOverrides
						rowHeight={50}
						rowsPerPage={5}
					/>
				</div>
			</div>
		</CoreModal>
	);
}
